import { useCallback, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { NotificationPreferencesManager } from '@/lib/services/NotificationPreferencesManager';

export type NotificationStatusLabel = 'On' | 'Off' | undefined;

/**
 * Combined notification status for the settings row label.
 * 'On' requires BOTH the OS-level permission and the in-app master toggle.
 * Re-checks whenever the consuming screen gains focus (e.g. after returning
 * from the notification-settings submenu or system settings).
 */
export function useNotificationStatusLabel(): NotificationStatusLabel {
  const [label, setLabel] = useState<NotificationStatusLabel>(undefined);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const manager = NotificationPreferencesManager.getInstance();

      (async () => {
        await manager.initialize();
        const { granted } = await manager.checkSystemPermissions();
        const preferences = manager.getPreferences();
        if (!cancelled) {
          setLabel(granted && preferences.notificationsEnabled ? 'On' : 'Off');
        }
      })().catch(() => {
        if (!cancelled) setLabel(undefined);
      });

      return () => {
        cancelled = true;
      };
    }, [])
  );

  return label;
}
